import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Pencil, Trash2, PlusCircle } from "lucide-react";
import Swal from "sweetalert2";
import { useSettings } from "../../context/SettingsContext";
const AddressesTab = () => {
  const { token, user } = useSelector((state) => state.auth);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
const { settings } = useSettings();
  const [newAddress, setNewAddress] = useState({
    hno: "",
    street_address: "",
    suburb: "",
    pincode: "",
    city: "",
    state: "",
    country: "India",
  });

  // Fetch addresses
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${window.BASE_API_URL}address/${token}`);
      if (res.data.success) {
        setAddresses(res.data.data || []);
      } else {
        toast.error("Failed to load addresses");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching addresses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewAddress((prev) => ({ ...prev, [name]: value }));
  };

  // Add or Update Address
  const handleSaveAddress = async () => {
    try {
      if (!newAddress.hno || !newAddress.street_address || !newAddress.pincode || !newAddress.state) {
        toast.error("Please fill all required fields");
        return;
      }

      const url = editingAddress
        ? `${window.BASE_API_URL}address/update`
        : `${window.BASE_API_URL}address/add`;

      const payload = { ...newAddress, token };

      const res = await axios.post(url, payload);

      if (res.data.success) {
        toast.success(editingAddress ? "Address updated successfully!" : "Address added successfully!");
        setShowAddressModal(false);
        setEditingAddress(null);
        setNewAddress({
          hno: "",
          street_address: "",
          suburb: "",
          pincode: "",
          city: "",
          state: "",
          country: "India",
        });
        fetchAddresses();
      } else {
        toast.error(res.data.message || "Failed to save address");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error saving address");
    }
  };

  // Edit Address
  const handleEdit = (address) => {
    setEditingAddress(address);
    setNewAddress({ ...address });
    setShowAddressModal(true);
  };

  // Delete Address
const handleDelete = async (id) => {
  const result = await Swal.fire({
    title: "Are you sure?",
    text: "This address will be permanently deleted.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it!",
    cancelButtonText: "Cancel",
  });

  if (!result.isConfirmed) return;

  try {
    const res = await axios.post(`${window.BASE_API_URL}address/delete/${id}`, { token });

    if (res.data.success) {
      Swal.fire("Deleted!", "Your address has been removed.", "success");
      fetchAddresses();
    } else {
      Swal.fire("Error", "Failed to delete address", "error");
    }
  } catch (error) {
    console.error(error);
    Swal.fire("Error", "Something went wrong", "error");
  }
};


  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-amber-700"  style={{ color: settings?.cart_btn_color || "#111827" }}>My Addresses</h2>
        <button
          onClick={() => {
            setEditingAddress(null);
            setNewAddress({
              hno: "",
              street_address: "",
              suburb: "",
              pincode: "",
              city: "",
              state: "",
              country: "India",
            });
            setShowAddressModal(true);
          }}
          className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-full hover:bg-amber-700 transition"
        >
          <PlusCircle className="w-5 h-5" /> Add New
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading addresses...</div>
      ) : addresses.length === 0 ? (
        <div className="text-center py-10 text-gray-500">No saved addresses yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {addresses.map((addr) => (
            <div
              key={addr.id}
              className="border border-amber-100 rounded-xl p-5 shadow-sm bg-white hover:shadow-md transition"
            >
              <p className="text-gray-800 font-semibold mb-2">
                {addr.hno}, {addr.street_address}
              </p>
              <p className="text-gray-600 text-sm">
                {addr.suburb}, {addr.city}, {addr.state}, {addr.country}
              </p>
              <p className="text-gray-500 text-sm mt-1">Pincode: {addr.pincode}</p>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => handleEdit(addr)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg border-gray-300 hover:bg-gray-100"
                >
                  <Pencil className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(addr.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-96 max-h-[90vh] overflow-y-auto border border-amber-100">
            <h3 className="text-lg font-semibold mb-4 text-amber-700">
              {editingAddress ? "Edit Address" : "Add New Address"}
            </h3>

            <div className="space-y-3">
              {[
                { name: "hno", label: "House / Flat No", required: true },
                { name: "street_address", label: "Street Name", required: true },
                { name: "suburb", label: "Suburb", required: false },
                { name: "pincode", label: "Postcode", required: true },
                { name: "city", label: "City" },
                { name: "state", label: "State", required: true },
                { name: "country", label: "Country" },
              ].map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium mb-1">
                    {field.label}{" "}
                    {field.required && <span className="text-red-600">*</span>}
                  </label>
                  <input
                    type={field.name === "pincode" ? "number" : "text"}
                    name={field.name}
                    placeholder={field.label}
                    className="border rounded-lg p-2 w-full focus:ring-2 focus:ring-amber-400 outline-none"
                    value={newAddress[field.name] || ""}
                    onChange={handleChange}
                    required={field.required}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition"
                onClick={() => setShowAddressModal(false)}
              >
                Cancel
              </button>
              <button
              style={{
                  background: settings?.cart_btn_color || "#000000ff",
                  color: settings?.cart_btn_text_color || "#ffffff",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    settings?.cart_btn_hover || "#f59e0b";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    settings?.cart_btn_color || "#ffffff";
                }}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition"
                onClick={handleSaveAddress}
              >
                {editingAddress ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressesTab;
